/**
 * Cohort review queue — every member of an Investigator run, match or not.
 *
 * A 187-member answer is not reviewable as citation chips in prose, and it is
 * not reviewable as 187 browser tabs either. It is a LIST YOU WALK: filter,
 * pick a row, read its verdict and evidence, go back, next row.
 *
 * COVERAGE IS THE HEADLINE, not the match count. "21 matched" alone cannot be
 * audited — it says nothing about whether the other 166 were inspected and
 * rejected, or skipped, or never reached. So the banner leads with
 * analysed/total and says plainly when coverage is not provable. That mirrors
 * the lexical census, where zero matches only means absence alongside
 * `complete: true`.
 *
 * Evidence comes from the durable per-member `evidence_json`, NOT the chat
 * answer's claim map — that lives in Redis on a ~30 minute TTL keyed to an
 * ephemeral answer id, and a cohort review runs for an afternoon.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

export interface CohortCoverage {
  total: number;
  analysed: number;
  matched: number;
  not_matched: number;
  complete: boolean;
  job_status: string;
  returned: number;
}

export interface CohortMember {
  drug_id: number;
  drug_name: string;
  application_number?: string | null;
  match: boolean;
  score: number;
  rank?: number | null;
  analysis?: string | null;
  evidence?: Record<string, unknown> | null;
}

type Verdict = 'all' | 'match' | 'no_match';

const PAGE = 25;

/** How often a live run is re-asked. Cohort runs last minutes to an afternoon,
 *  so this is about the pane not looking dead, not about precision. */
const POLL_MS = 3_000;

/** Statuses after which nothing more can be learned, from
 *  `RESEARCH_JOB_STATUSES` (db/models_research.py). The live ones are
 *  `pending` / `running` / `analyzing` / `resuming`; these three are the end.
 *  Kept SEPARATE from `finished` below on purpose: `finished` gates the handover
 *  to CEPI and must stay exactly `complete`, because an errored or cancelled run
 *  must never be attached — but it must still stop being polled. */
const TERMINAL_STATUSES = new Set(['complete', 'error', 'cancelled']);

export const CohortResultQueue: React.FC<{
  jobId: string;
  onOpenSource?: ((member: CohortMember) => void) | undefined;
  /**
   * Fired ONCE, when this run is over and has rows.
   *
   * There is no [Use in CEPI] button any more. The panel attaches the focused
   * tab's last completed run, so this pane's job is to say when that run
   * finished — not to ask the user to commit it.
   *
   * The button it replaces held a fact that does not live in this component:
   * it was local `useState(false)`, set true on attach and never set back
   * (there was no `setAttached(false)` in the file). A detach happens
   * elsewhere, so it never arrived: the user detached and the button stayed
   * dead for the life of the instance (prod, 2026-09-21). The mirror failure
   * was as bad — a remount re-armed it as if nothing had been attached.
   */
  onRunComplete?: ((result: { jobId: string; count: number; queryText: string }) => void) | undefined;
  /** The criterion this run was started with, carried on the report. */
  criterion?: string | undefined;
}> = ({ jobId, onOpenSource, onRunComplete, criterion = '' }) => {
  const [verdict, setVerdict] = useState<Verdict>('all');
  const [coverage, setCoverage] = useState<CohortCoverage | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [selected, setSelected] = useState<CohortMember | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A fetch that FAILED, as opposed to one that returned a live run. Polling
  // stops on it: this pane's requests once resolved to /api/api/... and 404'd
  // every time, and an unconditional retry loop would have turned that into a
  // self-inflicted hammering of the proxy. The banner has already said so.
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (opts?: { background?: boolean }) => {
    // A background tick must be INVISIBLE. `loading` swaps the member list for
    // "Loading…", so polling without this flag would blank the list the user is
    // reading every few seconds.
    const background = opts?.background === true;
    if (!background) setLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken(false) : null;
      const res = await fetch(
        // NOT `/api/v2/...`. API_BASE_URL is the proxy function whose name
        // already maps to /api, so the extra segment resolved to /api/api/...
        // and 404'd every request this pane makes. Guarded now by
        // services/__tests__/apiBasePrefix.test.ts, which scans source rather
        // than a list of call sites — enumerating callers is what let this
        // one through.
        `${API_BASE_URL}/v2/research/${jobId}/members`
        + `?verdict=${verdict}&limit=${PAGE}`,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            ...getImpersonationHeaders(),
          },
        },
      );
      if (!res.ok) {
        // Say what happened, not what we GUESS it means. This used to read
        // "Cohort review is not enabled in this environment." on any 404 —
        // and the 404 was our own doubled /api prefix, on a run that had
        // completed with 121 matches. The message sent the user to check a
        // flag that was switched on the whole time, which is worse than
        // silence: a confident wrong explanation ends the investigation.
        setError(`Could not load results (HTTP ${res.status}).`);
        setFailed(true);
        return;
      }
      const data = await res.json();
      setCoverage(data.coverage ?? null);
      setMembers(Array.isArray(data.members) ? data.members : []);
      // NO setSelected(null) here. It was unreachable anyway — the detail view
      // replaces the filter row, so `verdict` cannot change while a member is
      // open — and under polling it would have become actively harmful,
      // closing the evidence the user is mid-way through reading.
    } catch {
      setError('Could not reach the server.');
      setFailed(true);
    } finally {
      if (!background) setLoading(false);
    }
  }, [jobId, verdict]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Ask again while the run is still going.
   *
   * REPORTED LIVE 2026-09-24: a job that completed in 2m34s showed
   * "0 of 0 inspected · status: pending · Still running" indefinitely, and the
   * user asked whether it was stuck. The effect above was the ONLY fetch, and
   * `load` is memoised on [jobId, verdict] — so the pane asked once, while the
   * job was still `pending`, and never again. Clicking a verdict filter was the
   * only refresh, because that rebuilt `load`.
   *
   * The severity is not cosmetic. `onRunComplete` below fires on
   * `job_status === 'complete'`, a status this pane could never observe a
   * second time — so a run that finished after mount was never handed to CEPI,
   * and the conversation went on answering as if nothing were attached.
   */
  const settledStatus = coverage?.job_status;
  const isOver = settledStatus !== undefined
    && TERMINAL_STATUSES.has(settledStatus);

  useEffect(() => {
    if (isOver || failed) return;
    const timer = setInterval(() => { void load({ background: true }); },
                              POLL_MS);
    return () => clearInterval(timer);
  }, [isOver, failed, load]);

  /**
   * Only a FINISHED run may be handed to CEPI.
   *
   * Gated on `job_status`, not on `coverage.complete`. Those are different
   * claims: `complete: false` means coverage is not PROVABLE — the banner
   * says so honestly — which is not a reason to withhold verdicts the user
   * may still want. `job_status` is whether the run is over. Attaching one
   * that is not hands CEPI a partial or empty set, and the next answer reads
   * "the investigation found nothing": a wrong answer wearing a right
   * answer's clothes.
   *
   * Unknown coverage (the fetch has not landed) counts as NOT finished. The
   * conservative direction is the safe one here — and it is now the ONLY
   * guard, since no human confirms the handover any more.
   */
  const finished = coverage?.job_status === 'complete';
  /** Over, one way or another. Only `finished` may hand the run to CEPI; `ended`
   *  just retires the "Still running" notice so a stopped run is not mislabelled. */
  const ended = ['complete', 'error', 'cancelled'].includes(coverage?.job_status ?? '');

  /**
   * Report the finished run exactly once.
   *
   * `load()` polls, so `finished` stays true across many renders; the ref is
   * what stops each poll tick from re-reporting and re-attaching. An empty
   * run is NOT reported — zero matched products is a scope CEPI's resolver
   * refuses ("the attached result names no products"), so handing it over
   * would produce an attachment that is dead on arrival.
   */
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!finished || reportedRef.current || !onRunComplete) return;
    const count = coverage?.matched ?? 0;
    if (count <= 0) return;
    reportedRef.current = true;
    onRunComplete({ jobId, count, queryText: criterion });
  }, [finished, coverage, jobId, criterion, onRunComplete]);

  if (selected) {
    return (
      <div className="flex h-full flex-col" data-testid="cohort-member-detail">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="self-start px-3 py-2 text-xs text-indigo-700 hover:underline"
        >
          ← Back to results
        </button>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {selected.drug_name}
            </h3>
            <VerdictPill match={selected.match} />
          </div>
          {selected.analysis && (
            <>
              <div className="mt-3 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Reason
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                {selected.analysis}
              </p>
            </>
          )}
          <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Evidence
          </div>
          {selected.evidence
            ? (
              <>
                <pre className="mt-1 max-h-72 overflow-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">
                  {JSON.stringify(selected.evidence, null, 2)}
                </pre>
                {onOpenSource && (
                  <button
                    type="button"
                    onClick={() => onOpenSource(selected)}
                    className="mt-2 rounded-md border border-indigo-300 px-2.5 py-1 text-xs text-indigo-700 hover:bg-indigo-50"
                  >
                    View source
                  </button>
                )}
              </>
            )
            : (
              <p className="mt-1 text-sm text-slate-500">
                No stored evidence for this member.
              </p>
            )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="cohort-result-queue">
      {coverage && <CoverageBanner coverage={coverage} />}

      {/* No commit button. While the run is still going, say so — the user
          would otherwise have no way to tell why the chip has not moved. */}
      {onRunComplete && !ended && (
        <div className="px-4 pt-2 text-[11px] text-slate-500"
             data-testid="cohort-still-running">
          Still running — you can answer from these results in CEPI once it finishes.
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 px-4 py-2">
        {([
          ['all', `All ${coverage ? coverage.analysed : ''}`.trim()],
          ['match', `Matched ${coverage ? coverage.matched : ''}`.trim()],
          ['no_match', `Not matched ${coverage ? coverage.not_matched : ''}`.trim()],
        ] as [Verdict, string][]).map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setVerdict(v)}
            data-testid={`cohort-filter-${v}`}
            className={`rounded-full px-2.5 py-1 text-xs ${
              verdict === v
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {loading && <p className="py-6 text-center text-sm text-slate-500">Loading…</p>}
        {error && !loading && (
          <p className="py-6 text-center text-sm text-amber-800">{error}</p>
        )}
        {!loading && !error && members.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500">
            No members in this view.
          </p>
        )}
        {!loading && !error && members.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-1.5 font-medium">Product</th>
                <th className="py-1.5 font-medium">Verdict</th>
                <th className="py-1.5 font-medium">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr
                  key={m.drug_id}
                  onClick={() => setSelected(m)}
                  data-testid="cohort-row"
                  className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-1.5 pr-2 text-slate-800">{m.drug_name}</td>
                  <td className="py-1.5 pr-2"><VerdictPill match={m.match} /></td>
                  <td className="py-1.5 text-xs text-slate-500">
                    {m.evidence ? 'sources' : 'inspected'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {coverage && coverage.returned < coverage.analysed && !loading && !error && (
          <p className="pt-2 text-center text-[11px] text-slate-500">
            Showing {coverage.returned} of {coverage.analysed}.
          </p>
        )}
      </div>
    </div>
  );
};

const VerdictPill: React.FC<{ match: boolean }> = ({ match }) => (
  <span
    className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
      match ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
    }`}
  >
    {match ? 'Match' : 'No match'}
  </span>
);

const CoverageBanner: React.FC<{ coverage: CohortCoverage }> = ({ coverage }) => (
  <div
    data-testid="cohort-coverage"
    data-complete={String(coverage.complete)}
    className={`border-b px-4 py-2 text-xs ${
      coverage.complete
        ? 'border-slate-200 bg-slate-50 text-slate-700'
        : 'border-amber-200 bg-amber-50 text-amber-900'
    }`}
  >
    <span className="tabular-nums font-medium">
      {coverage.analysed} of {coverage.total} inspected
    </span>
    {' · '}
    <span className="tabular-nums">{coverage.matched} matched</span>
    {!coverage.complete && (
      /* The honest case. A job can report status "complete" while most of the
         cohort has no verdict row — saying "21 matched" over that would be a
         completeness claim the data cannot support. */
      <span className="ml-1">
        — coverage is not provable for this run (status: {coverage.job_status}).
      </span>
    )}
  </div>
);

export default CohortResultQueue;
